import { useCallback, useEffect, useRef, useState } from 'react';

import { LoadingState } from '@atlas/base';
import { Bus, NotificationManager, useApplicationState, useSettings } from '@atlas/core';

import { getBonusActivationToastOptions } from 'common/utils/bonusActivationToast';
import {
  BONUS_CONFLICT_ERROR_CODES,
  BONUS_CONFLICT_ERROR_MESSAGES,
  bonusTypes,
  FREE_SPIN_LOAD_STATUSES,
} from 'src/common/constants';
import useBonusesService from 'src/hooks/useBonusesService';

import { mapAvailableBonusForUi, mapClientBonusForUi, mapFreeSpinForUi } from './sdkUiMappers';

const hasLoadFinished = state => state === LoadingState.success || state === LoadingState.failed;

const PENDING_BONUS_POLL_MS = 1000;
const PENDING_BONUS_POLL_MAX_ATTEMPTS = 5;
const TRANSIENT_BONUS_STATUSES = new Set(['PENDING', 'PENDING_ACTIVATION']);

const getStringValues = values => values.filter(value => typeof value === 'string');

const getErrorValues = error => getStringValues([error?.code, error?.message, error?.error]);

const getNestedErrorValues = error => getStringValues([error?.code, error?.message]);

const getBonusConflictErrorValues = error => {
  if (typeof error === 'string') {
    return [error];
  }

  return [
    ...getErrorValues(error),
    ...getNestedErrorValues(error?.error),
    ...getErrorValues(error?.data),
    ...getErrorValues(error?.response),
  ];
};

const getBlockedBonusInstanceId = error =>
  error?.blockedBonusInstanceId ??
  error?.data?.blockedBonusInstanceId ??
  error?.response?.blockedBonusInstanceId ??
  error?.error?.blockedBonusInstanceId;

const getBonusProductType = bonus => bonus?.bonus?.productType ?? bonus?.productType;

const isBonusConflictError = error => {
  const errorValues = getBonusConflictErrorValues(error);
  const conflictErrorValues = [...BONUS_CONFLICT_ERROR_CODES, ...BONUS_CONFLICT_ERROR_MESSAGES];

  return (
    getBlockedBonusInstanceId(error) != null ||
    errorValues.some(value =>
      conflictErrorValues.some(conflictErrorValue => value.includes(conflictErrorValue)),
    )
  );
};

const getBlockedBonus = ({ blockedBonusInstanceId, bonus, bonusEntities, error }) => {
  const entities = [...bonusEntities.values()];

  if (blockedBonusInstanceId) {
    return entities.find(el => String(el?.id) === String(blockedBonusInstanceId));
  }

  const activeProductType =
    bonus?.productType ||
    getBonusProductType(entities.find(el => String(el?.bonus?.id) === String(error?.bonusId)));

  const activeBonus = entities.find(el => {
    const status = String(el?.status || '').toUpperCase();
    const isActiveBonus = status === 'IN_PROGRESS' || status === 'ACTIVATED';

    return isActiveBonus && getBonusProductType(el) === activeProductType;
  });

  return (
    activeBonus ||
    entities.find(
      el => String(el?.id) !== String(bonus?.id) && getBonusProductType(el) === activeProductType,
    )
  );
};

const useCombinedBonuses = () => {
  const {
    account: { currency: accountCurrency } = {},
    authenticated,
    wallet,
  } = useApplicationState();
  const { lang } = useSettings();
  const language = String(lang || '').toUpperCase();
  const currency = wallet?.currency || accountCurrency;

  const [clientBonuses, setClientBonuses] = useState([]);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [freespins, setFreespins] = useState([]);
  const [bonusesLoading, setBonusesLoading] = useState(true);
  const [freespinsLoading, setFreespinsLoading] = useState(true);

  // Keep the SDK entity instances so we can call .activate() / .deactivate() on them
  const bonusEntitiesRef = useRef(new Map());
  const freespinEntitiesRef = useRef(new Map());
  const pendingReloadTimerRef = useRef(null);

  const bonusesService = useBonusesService();

  const freeSpinService = bonusesService?.freeSpins;

  useEffect(() => {
    if (!authenticated || !currency || !bonusesService) {
      setBonusesLoading(false);
      setClientBonuses([]);
      setAvailableTemplates([]);
      return undefined;
    }

    setBonusesLoading(true);

    const onData = ([templates, instances]) => {
      const entities = new Map();
      templates.forEach(t => entities.set(`template:${t.id}`, t));
      instances.forEach(i => entities.set(`instance:${i.id}`, i));
      bonusEntitiesRef.current = entities;

      setAvailableTemplates(templates.map(t => mapAvailableBonusForUi(t, language)));
      setClientBonuses(instances.map(i => mapClientBonusForUi(i, language)));
    };

    const onLoading = state => {
      if (hasLoadFinished(state)) setBonusesLoading(false);
    };

    bonusesService.on('data', onData);
    bonusesService.on('loading', onLoading);
    bonusesService
      .load()
      .then(result => {
        if (result) onData(result);
      })
      .catch(() => {})
      .finally(() => setBonusesLoading(false));

    return () => {
      bonusesService.off('data', onData);
      bonusesService.off('loading', onLoading);
    };
  }, [authenticated, bonusesService, currency, language]);

  useEffect(() => {
    if (!authenticated || !currency || !freeSpinService) {
      setFreespinsLoading(false);
      setFreespins([]);
      return undefined;
    }

    setFreespinsLoading(true);

    const onData = loaded => {
      const entities = new Map();
      loaded.forEach(fs => entities.set(fs.id, fs));
      freespinEntitiesRef.current = entities;
      setFreespins(loaded.map(fs => mapFreeSpinForUi(fs, language)));
    };

    const onLoading = state => {
      if (hasLoadFinished(state)) setFreespinsLoading(false);
    };

    freeSpinService.on('data', onData);
    freeSpinService.on('loading', onLoading);
    freeSpinService
      .load(FREE_SPIN_LOAD_STATUSES)
      .then()
      .finally(() => setFreespinsLoading(false));

    return () => {
      freeSpinService.off('data', onData);
      freeSpinService.off('loading', onLoading);
    };
  }, [authenticated, currency, freeSpinService, language]);

  const pollPendingBonuses = useCallback(
    function poll(attempt) {
      if (pendingReloadTimerRef.current) {
        clearTimeout(pendingReloadTimerRef.current);
        pendingReloadTimerRef.current = null;
      }

      const hasTransientInstance = bonusesService?.clientBonuses?.some(bonus =>
        TRANSIENT_BONUS_STATUSES.has(bonus.status),
      );

      if (!hasTransientInstance || attempt >= PENDING_BONUS_POLL_MAX_ATTEMPTS) return;

      pendingReloadTimerRef.current = setTimeout(() => {
        pendingReloadTimerRef.current = null;
        bonusesService.load().finally(() => poll(attempt + 1));
      }, PENDING_BONUS_POLL_MS);
    },
    [bonusesService],
  );

  const reloadBonuses = useCallback(async () => {
    if (!authenticated || !currency || !bonusesService) return;
    await bonusesService.load();
    pollPendingBonuses(0);
  }, [authenticated, bonusesService, currency, pollPendingBonuses]);

  useEffect(
    () => () => {
      if (pendingReloadTimerRef.current) clearTimeout(pendingReloadTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!authenticated || !bonusesService) return undefined;

    const handleBonusInstanceStatusChanged = () => {
      reloadBonuses();
      freeSpinService?.load(FREE_SPIN_LOAD_STATUSES);
    };

    Bus.on(Bus.events.bonus.bonusInstanceStatusChanged, handleBonusInstanceStatusChanged);

    return () =>
      Bus.off(Bus.events.bonus.bonusInstanceStatusChanged, handleBonusInstanceStatusChanged);
  }, [authenticated, bonusesService, freeSpinService, reloadBonuses]);

  const activateBonus = useCallback(
    async (bonus, onSuccessResult) => {
      if (!bonusesService) return;
      const entity =
        bonusEntitiesRef.current.get(`instance:${bonus.id}`) ||
        bonusEntitiesRef.current.get(`template:${bonus.id}`);
      if (!entity) return;

      try {
        await bonusesService.activateBonus(entity);
        NotificationManager.toast(getBonusActivationToastOptions(bonus, lang.toUpperCase()));
        await reloadBonuses();
        if (onSuccessResult) onSuccessResult();
      } catch (err) {
        if (isBonusConflictError(err)) {
          const blockedBonusInstanceId = getBlockedBonusInstanceId(err);
          const blockedBonus = getBlockedBonus({
            blockedBonusInstanceId,
            bonus,
            bonusEntities: bonusEntitiesRef.current,
            error: err,
          });
          const conflictingBonusId = blockedBonusInstanceId ?? blockedBonus?.id;
          const productType = getBonusProductType(blockedBonus) ?? bonus?.productType;

          NotificationManager.display({
            data: { conflictingBonusId, productType },
            type: 'bonusConflict',
          });
        } else {
          NotificationManager.notify({
            data: err,
            message: err.code,
            status: 'error',
            type: 'notification',
          });
        }
      }
    },
    [bonusesService, reloadBonuses],
  );

  const deactivateBonus = useCallback(
    async bonus => {
      if (!bonusesService) return;
      const entity = bonusEntitiesRef.current.get(`instance:${bonus.id}`);
      if (!entity) return;

      NotificationManager.display({
        data: {
          bonus,
          deactivateBonus: async () => {
            try {
              await bonusesService.cancelBonus(entity);
              await reloadBonuses();
            } catch (err) {
              NotificationManager.notify({
                data: err,
                message: err.code,
                status: 'error',
                type: 'notification',
              });
              throw err;
            }
          },
          kind: 'bonus',
        },
        type: 'bonusCancel',
      });
    },
    [bonusesService, reloadBonuses],
  );

  const activateFreespin = useCallback(
    async (freeSpinId, onSuccessResult) => {
      if (!freeSpinService) return;
      const entity = freespinEntitiesRef.current.get(freeSpinId);
      if (!entity?.activate) return;

      try {
        await entity.activate();
        NotificationManager.notify({
          message: 'bonus_activated',
          status: 'success',
          type: 'notification',
        });
        await freeSpinService.load(FREE_SPIN_LOAD_STATUSES);
        if (onSuccessResult) onSuccessResult();
      } catch (err) {
        // Unlocked -> Activate conflict (FT-8094): another IN_PROGRESS bonus of the same
        // product blocks activation. Surface the shared conflict popup, else a generic error.
        if (isBonusConflictError(err)) {
          const blockedBonusInstanceId = getBlockedBonusInstanceId(err);
          const blockedBonus = getBlockedBonus({
            blockedBonusInstanceId,
            bonus: entity,
            bonusEntities: bonusEntitiesRef.current,
            error: err,
          });
          const conflictingBonusId = blockedBonusInstanceId ?? blockedBonus?.id;
          const productType = getBonusProductType(blockedBonus) ?? entity?.productType;

          NotificationManager.display({
            data: { conflictingBonusId, productType },
            type: 'bonusConflict',
          });
        } else {
          NotificationManager.notify({
            data: err,
            message: err.code,
            status: 'error',
            type: 'notification',
          });
        }
      }
    },
    [freeSpinService],
  );

  const cancelFreespin = useCallback(
    freeSpinId => {
      if (!freeSpinService) return;
      const entity = freespinEntitiesRef.current.get(freeSpinId);
      if (!entity?.cancel) return;

      // Reuse the shared bonus cancel confirmation popup (FT-8086); on confirm the SDK
      // FreeSpin entity cancels itself (client-freespin:cancel) and the feed reloads.
      NotificationManager.display({
        data: {
          bonus: {
            activeSpins: entity.numberOfSpins,
            id: entity.id,
            name: entity.name,
            nameHighlighted: entity.nameHighlighted,
            type: entity.subType === 'DEPOSIT' ? bonusTypes.DEPOSIT_FREESPIN : bonusTypes.FREESPIN,
          },
          deactivateBonus: async () => {
            try {
              await entity.cancel();
              await freeSpinService.load(FREE_SPIN_LOAD_STATUSES);
            } catch (err) {
              NotificationManager.notify({
                data: err,
                message: err.code,
                status: 'error',
                type: 'notification',
              });
            }
          },
          kind: 'freespin',
        },
        type: 'bonusCancel',
      });
    },
    [freeSpinService],
  );

  const loading = bonusesLoading || freespinsLoading;

  const totalCount = clientBonuses.length + availableTemplates.length + freespins.length;
  const showCombinedEmpty = !loading && totalCount === 0;

  return {
    activateBonus,
    activateFreespin,
    availableTemplates,
    cancelFreespin,
    clientBonuses,
    deactivateBonus,
    freespins,
    loading,
    showCombinedEmpty,
    wallet: wallet ?? {},
  };
};

export default useCombinedBonuses;
